import { GlobalMessages } from '@shared/localization/messages';
import type { Ref } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type SelectInstance } from 'react-select';

import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import type { ApiPermission, UserResponse } from '@/api/schema/data-contracts';
import LiveSearchSelect from '@/components/atoms/LiveSearchSelect';
import { useMessages } from '@/hooks/messages';
import type { SelectOption } from '@/types';

interface UserAutocompleteFieldProps {
  userIds?: string | string[];
  name: string;
  placeholder?: string;
  isDisabled?: boolean;
  isMulti?: boolean;
  permissions?: ApiPermission[];
  onChange: (userIds: string | string[] | null) => void;
  onBlur: () => void;
  'data-testid'?: string;
  ref?: Ref<SelectInstance>;
}

export const UserAutocompleteField = ({
  userIds,
  name,
  placeholder,
  isDisabled,
  isMulti,
  permissions,
  onChange,
  onBlur,
  'data-testid': dataTestId,
  ref,
}: UserAutocompleteFieldProps) => {
  const globalMessages = useMessages(GlobalMessages);
  const [search, setSearch] = useState('');
  const [loadData, setLoadData] = useState(false);
  const { useGetUser, useGetUsers } = useUserQueryActions();
  const { data: selectedUser, isLoading: isLoadingUser } = useGetUser(
    !isMulti && typeof userIds === 'string' ? userIds : undefined,
  ) as { data: UserResponse | undefined; isLoading: boolean };
  const { data: users, isLoading: isLoadingUsers } = useGetUsers(
    { fulltextSearch: search, permissions },
    isMulti || loadData,
  ) as { data: UserResponse[] | undefined; isLoading: boolean };

  // Multi-select chips must not disappear when the live search results are
  // reset (e.g. react-select clears the input text right after a selection,
  // which re-fetches with an empty search term). Accumulate every user seen
  // in any search-results response, keyed by id, so a chip stays renderable
  // even after its user drops out of the current results page.
  const [seenUsers, setSeenUsers] = useState<Record<string, UserResponse>>({});

  useEffect(() => {
    if (!isMulti || !users?.length) {
      return;
    }
    setSeenUsers((previous) => {
      const next = { ...previous };
      let hasNewUser = false;
      users.forEach((u) => {
        if (next[u.id] !== u) {
          next[u.id] = u;
          hasNewUser = true;
        }
      });
      return hasNewUser ? next : previous;
    });
  }, [isMulti, users]);

  const displayName = useCallback(
    (u: UserResponse) => {
      return `${u.forename} ${u.surname}${u.isActive ? '' : ` (${globalMessages.inactive()})`}`;
    },
    [globalMessages],
  );

  const selectedOptions = useMemo(() => {
    if (selectedUser) {
      return {
        value: selectedUser.id,
        label: displayName(selectedUser),
      } as SelectOption;
    }
    if (isMulti) {
      if (!userIds) {
        return undefined;
      }
      const ids = Array.isArray(userIds) ? userIds : [userIds];
      return ids
        .map((id) => seenUsers[id])
        .filter((u): u is UserResponse => Boolean(u))
        .map<SelectOption>((u) => ({
          value: u.id,
          label: displayName(u),
        }));
    }
    return users
      ?.filter((u) => userIds?.includes(u.id))
      .map<SelectOption>((u) => ({
        value: u.id,
        label: displayName(u),
      }));
  }, [selectedUser, isMulti, userIds, seenUsers, users, displayName]);

  const userOptions = useMemo(() => {
    if (users) {
      return users.map((user) => ({
        value: user.id,
        label: displayName(user),
      }));
    }
    return [];
  }, [users, displayName]);

  return (
    <LiveSearchSelect
      ref={ref}
      value={selectedOptions}
      placeholder={placeholder}
      onValueSelect={(selectedUserIds) => {
        if (Array.isArray(selectedUserIds) && !isMulti) {
          throw new Error('Unsupported multiselect here');
        }
        return onChange(selectedUserIds);
      }}
      onInputChange={(input) => setSearch(input)}
      onBlur={onBlur}
      onFocus={() => setLoadData(true)}
      name={name}
      isLoading={isLoadingUser || isLoadingUsers}
      options={userOptions}
      isDisabled={isDisabled}
      isMulti={isMulti}
      data-testid={dataTestId}
    />
  );
};
